/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { chain, includes, range, find, uniqBy } from "lodash";
import React, { useEffect, useState, useRef } from "react";
import { Grid, Typography, FormGroup, FormControlLabel } from "@mui/material";
import { Viewer } from "components/auth/types";
import { STATUSES_INACTIVE } from "model/interest/status";
import { formatAsCurrency } from "pages/dashboard/utils";
import { Button } from "components/haven-design";
import { SidebarFilters, SidebarFilters_interestListsById_sidebarFilters as SidebarFilter } from "types/gql-types";
import { PossibleClientRemovalReasons, PossiblePropManagerRemovalReasons } from "components/structured-reasons";
import { useInterestsTable } from "../interests-table/table-context";
import { useSidebarFilters } from "../sidebar-filters";
import {
  AmiRadioInput,
  FilterCheckbox,
  FilterCheckboxWithInverse,
  FilterEqualityInput,
  FilterFacetCheckbox,
  FilterOver55,
  FilterRange,
  FilterDateRange,
  IncomeFilter,
  FilterMultiSelect,
} from "./filters";
import FilterStatuses from "./filters-statuses";
import Heading from "./heading";
import InterestListSelector from "./interest-list-selector";
import { FiltersState } from "./state";
import { interestListAmiRanges } from "./ami-ranges";
import Duplicates from "./duplicates";
import FilterLogicControl from "./filter-logic-control";

const stripVerboseLabels = (text: string) =>
  chain(text)
    .replace("Does anyone in the household currently live", "Live")
    .replace("Are you a current", "Current")
    .replace("Are you currently", "Currently")
    .value();

const getCustomLabel = (filters: SidebarFilter[], name: string) => {
  const [filter] = filters.filter((x) => x.name === name);
  if (filter) return stripVerboseLabels(filter.label);
  return "";
};

const renderAllCustomPreferences =
(qty: number, filters: SidebarFilter[], filterList: (keyof FiltersState)[]) =>
  range(1, qty + 1).map((i) => {
    const name = `custom_pref_${i}` as keyof FiltersState;
    const customLabel = getCustomLabel(filters, name);
    const fallbackLabel = `Custom Preference #${i}`;
    const label = customLabel || fallbackLabel;
    return (
      <div key={i}>
        <FilterCheckboxWithInverse
          name={name} label={label}
          inverseLabel={`NOT ${label}`}
          filters={filterList}
        />
      </div>
    );
  });


type Props = {
  viewer: Viewer;
  data: SidebarFilters;
  listId: string;
}

const InterestsSidebarFilters: React.FC<Props> = ({
  listId, viewer, data: { interestListsById },
}) => {
  const { deselectAllInterests } = useInterestsTable();
  const {
    search,
    addFilter,
    clearFilters,
    setInterestListIds,
    interestListIds,
  } = useSidebarFilters();

  const sidebarFilters = uniqBy(interestListsById.map((x) => x.sidebarFilters).flat(), "name");
  const stages = uniqBy(interestListsById.map((x) => x.property.stages).flat().filter((stage) => stage.enabled), "name");
  const features = uniqBy(interestListsById.map((x) => x.property.features).flat(), "name");

  const availableStages = stages.map((stage) => stage.name);
  const activeStages = availableStages.filter((stage) =>
    !(STATUSES_INACTIVE as string[]).includes(stage));

  const [statusList, setStatusList] = useState<string[]>(activeStages);
  const previousStages = useRef<string[]>([]);

  useEffect(() => {
    if (!interestListsById?.length || !activeStages.length) {
      return;
    }

    const prev = previousStages.current;
    if (!prev.length || !prev.every((stage, i) => stage === activeStages[i])) {
      previousStages.current = activeStages;
      setStatusList(activeStages);
      addFilter("status", activeStages, "array");
      search();
    }
  }, [activeStages, interestListsById, addFilter]);

  const filterList = sidebarFilters.map((x) => x.name) as (keyof FiltersState)[];
  const currentRange = listId === "all" ? false : find(interestListAmiRanges, (x) => x.interestListId === listId);
  const amiRanges = currentRange ? currentRange.amiRange : [];


  const doResetFilters = () => {
    clearFilters();
    deselectAllInterests();
    setStatusList(activeStages);
    addFilter("status", activeStages, "array");
    search();
  };
  const onListChange = (newListId: string) => {
    if (newListId === "all" && viewer.lists) {
      setInterestListIds(viewer.lists.map((list) => list.id));
    } else {
      setInterestListIds([newListId]);
    }
    doResetFilters();
  };

  const onResetFilters = () => doResetFilters();

  return (
    <Grid container spacing={1}>
      <Grid size={{ xs: 12 }}>
        <InterestListSelector
          viewer={viewer}
          onListChange={onListChange}
          interestListId={listId}
        />
      </Grid>
      <Grid size={{ xs: 12 }} style={{ textAlign: "right", paddingTop: "1em" }}>
        {interestListIds.length === 1 && (
          <Duplicates interestListId={interestListIds[0]} />
        )}
        <Button color="secondary" size="S" onClick={() => onResetFilters()}>
          Clear Filters
        </Button>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Typography component="h4" variant="subtitle2" style={{ marginBottom: 2, marginTop: 25, fontSize: 14 }}>Status</Typography>
      </Grid>
      <FormGroup style={{ marginLeft: "42px" }}>
        <FilterStatuses
          state={{ statuses: statusList } as FiltersState}
          stages={availableStages}
          onChange={(statuses: string[]) => {
            setStatusList(statuses);
            addFilter("status", statuses, "array");
            search();
          }}
        />
      </FormGroup>

      {features.some((feature) => feature.name === "AMI_RANGE") && (
        <>
          <Grid size={{ xs: 12 }}>
            <Typography component="h4" variant="subtitle2" style={{ marginBottom: 2, marginTop: 25, fontSize: 14 }}>AMI</Typography>
          </Grid>
          <FormGroup sx={{ ml: 0.75 }}>
            {amiRanges.map((amiRange, i) => (
              <FormControlLabel
                key={i}
                control={(
                  <AmiRadioInput
                    label="Ami"
                    name="ami"
                    value={amiRange.value}
                    amiRange={amiRange}
                    filters={filterList}
                  />
                )}
                label={amiRange.value === "all" ? "All" : `${amiRange.value}% (${formatAsCurrency(amiRange.min)} - ${formatAsCurrency(amiRange.max)})`}
              />
            ))}
          </FormGroup>
        </>
      )}

      <Grid size={{ xs: 12 }} component="section" aria-label="Household income" id="household-income-filter">
        <Grid container>
          <Heading text="Household Income" name="heading_income" filters={filterList} />
          <IncomeFilter
            label="Income"
            name="household_income"
            filters={filterList}
            containerStyle={{ maxWidth: 230 }}
          />
          <FilterLogicControl
            lists={interestListsById}
            available={sidebarFilters.some((filter) => filter.name === "household_income")}
          />
        </Grid>
      </Grid>

      <Heading text="Household Size" name="heading_household_size" filters={filterList} />
      <FilterRange label="Household Size" name="household_count" filters={filterList} />

      <Heading text="Bedrooms" name="heading_bedrooms" filters={filterList} />
      <FormGroup>
        <FilterCheckbox name="filter_unit_size_sro" label="SRO" filters={filterList} wrapInGrid={false} />
        <FilterCheckbox name="filter_unit_size_studio" label="Studio" filters={filterList} wrapInGrid={false} />
        <FilterCheckbox name="filter_unit_size_1br" label="1 BR" filters={filterList} wrapInGrid={false} />
        <FilterCheckbox name="filter_unit_size_1br_den" label="1 BR + Den" filters={filterList} wrapInGrid={false} />
        <FilterCheckbox name="filter_unit_size_2br" label="2 BR" filters={filterList} wrapInGrid={false} />
        <FilterCheckbox name="filter_unit_size_3br" label="3 BR" filters={filterList} wrapInGrid={false} />
        <FilterCheckbox name="filter_unit_size_4br" label="4 BR" filters={filterList} wrapInGrid={false} />
        <FilterCheckbox name="filter_unit_size_5br" label="5 BR" filters={filterList} wrapInGrid={false} />
        <FilterCheckbox name="filter_unit_size_shared" label="Shared" filters={filterList} wrapInGrid={false} />
        <FilterCheckbox name="filter_unit_size_none" label="No unit size" filters={filterList} wrapInGrid={false} />
      </FormGroup>


      <Grid size={{ xs: 12 }}>
        <Typography component="h4" variant="subtitle2" style={{ marginBottom: 2, marginTop: 25, fontSize: 14 }}>User Account</Typography>
      </Grid>
      <FormGroup>
        <FilterCheckboxWithInverse
          showForEveryone
          name="has_user_id"
          label="Has Haven Connect account"
          inverseLabel="Does not have account"
          filters={filterList}
          wrapInGrid={false}
        />
      </FormGroup>

      <Grid size={{ xs: 12 }}>
        <Typography component="h4" variant="subtitle2" style={{ marginBottom: 2, marginTop: 25, fontSize: 14 }}>★ Starred</Typography>
      </Grid>
      <FormGroup>
        <FilterCheckboxWithInverse
          showForEveryone
          name="has_star"
          label="Starred Applications"
          inverseLabel="Not Starred"
          filters={filterList}
          wrapInGrid={false}
        />
      </FormGroup>

      {/* <h4 style={{ marginBottom: 2, marginTop: 25, fontSize: 14 }}>Document Uploads</h4>
      <FilterCheckboxWithInverse
        name="has_document"
        label="Applicant has documents"
        inverseLabel="Applicant does not have documents"
        filters={filterList}
      /> */}

      <Heading text="Subsidy" name="heading_subsidy" filters={filterList} />
      <FormGroup>
        {filterList.includes("household_has_housing_authority_voucher") && (
          <FilterCheckboxWithInverse
            showForEveryone
            name="household_has_housing_authority_voucher"
            label="Has housing authority voucher"
            inverseLabel="No housing authority voucher"
            filters={filterList}
            wrapInGrid={false}
          />
        )}
        {filterList.includes("household_receives_other_housing_assistance") && (
          <FilterCheckboxWithInverse
            showForEveryone
            name="household_receives_other_housing_assistance"
            label="Has other housing assistance"
            inverseLabel="No other housing assistance"
            filters={filterList}
            wrapInGrid={false}
          />
        )}
      </FormGroup>

      <Heading text="Preferences" name="heading_preferences" filters={filterList} />
      <FormGroup>
        {renderAllCustomPreferences(14, sidebarFilters, filterList)}
        <FilterCheckbox
          wrapInGrid={false}
          name="household_requires_impairment_mobility"
          label="Mobility impairment"
          filters={filterList}
        />
        <FilterCheckbox
          wrapInGrid={false}
          name="household_requires_impairment_visual"
          label="Visual impairment"
          filters={filterList}
        />
        <FilterCheckbox
          wrapInGrid={false}
          name="household_requires_impairment_hearing"
          label="Hearing impairment"
          filters={filterList}
        />
        <FilterCheckbox
          wrapInGrid={false}
          name="house_member_wheelchair"
          label="Wheelchair Accessible"
          filters={filterList}
        />
        <FilterCheckbox
          wrapInGrid={false}
          name="household_lives_works_in_alameda"
          label="Live/Work in Alameda"
          filters={filterList}
        />
        <FilterCheckbox
          wrapInGrid={false}
          name="household_lives_in_palo_alto"
          label="Lives in Palo Alto"
          filters={filterList}
        />
        <FilterCheckboxWithInverse
          name="household_lives_works_in_san_mateo"
          label="Live or work in the City of San Mateo"
          inverseLabel="Does not live or work in the City of San Mateo"
          filters={filterList}
          wrapInGrid={false}
        />
        <FilterCheckbox
          wrapInGrid={false}
          name="household_works_in_palo_alto"
          label="Works in Palo Alto"
          filters={filterList}
        />
        {includes(filterList, "household_lives_works_in_san_carlos") && (
          <FilterCheckboxWithInverse
            showForEveryone
            name="household_lives_works_in_san_carlos"
            label="San Carlos"
            inverseLabel="NOT in San Carlos"
            filters={filterList}
            wrapInGrid={false}
          />
        )}
        <FilterCheckbox
          name="household_all_full_time_students"
          label="Full time student(s)"
          filters={filterList}
          wrapInGrid={false}
        />
        <FilterCheckbox
          name="household_served_in_armed_forces"
          label="Veteran"
          filters={filterList}
          wrapInGrid={false}
        />
        <FilterCheckbox
          name="household_has_special_accommodations"
          label="Has Special Accommodation"
          filters={filterList}
          wrapInGrid={false}
        />
        <FilterFacetCheckbox
          name="applicant_rental_buy_pref"
          facetValue="rent"
          label="Interested in Renting?"
          filters={filterList}
          wrapInGrid={false}
        />
        <FilterFacetCheckbox
          name="applicant_rental_buy_pref"
          facetValue="buy"
          label="Interested in Buying?"
          filters={filterList}
          wrapInGrid={false}
        />
        <FilterFacetCheckbox
          name="applicant_rental_buy_pref"
          facetValue="both"
          label="Interested in Both?"
          filters={filterList}
          wrapInGrid={false}
        />
      </FormGroup>
      <Heading text="Applicant Age" name="heading_applicant_age" filters={filterList} />
      <FilterRange label="Date of Birth" name="applicant_dob" year filters={filterList} />
      <FilterOver55 label="Over 55" name="applicant_dob_over_55" filters={filterList} />

      <Heading text="AMI (excel imports only)" name="heading_ami" filters={filterList} />
      <FormGroup>
        <Grid size={{ xs: 12 }} style={{ maxWidth: 85 }}>
          <FilterEqualityInput label="Ami" name="household_ami" filters={filterList} />
        </Grid>
      </FormGroup>
      <FilterCheckbox name="ami_0br_40" label="0 BR 40%" filters={filterList} />
      <FilterCheckbox name="ami_0br_50" label="0 BR 50%" filters={filterList} />
      <FilterCheckbox name="ami_1br_40" label="1 BR 40%" filters={filterList} />
      <FilterCheckbox name="ami_1br_50" label="1 BR 50%" filters={filterList} />
      <FilterCheckbox name="ami_2br_40" label="2 BR 40%" filters={filterList} />
      <FilterCheckbox name="ami_2br_50" label="2 BR 50%" filters={filterList} />
      <FilterCheckbox name="ami_3br_40" label="3 BR 40%" filters={filterList} />
      <FilterCheckbox name="ami_3br_50" label="3 BR 50%" filters={filterList} />

      <Heading text="Position Number" name="heading_position_number" filters={filterList} />
      <FilterCheckbox
        name="has_position_number"
        label="Has Position Number"
        filters={filterList}
      />
      <FilterEqualityInput
        name="position_number"
        label="Lottery Position #"
        filters={filterList}
        inputStyle={{ maxWidth: "55" }}
      />

      <Heading text="Gender" name="heading_gender" filters={filterList} />
      <FormGroup>
        <FilterFacetCheckbox
          name="household_member_0_gender"
          facetValue="male"
          label="Male"
          filters={filterList}
          wrapInGrid={false}
        />
        <FilterFacetCheckbox
          name="household_member_0_gender"
          facetValue="female"
          label="Female"
          filters={filterList}
          wrapInGrid={false}
        />
        <FilterFacetCheckbox
          name="household_member_0_gender"
          facetValue="trans"
          label="Trans"
          filters={filterList}
          wrapInGrid={false}
        />
        <FilterFacetCheckbox
          name="household_member_0_gender"
          facetValue="nonbinary"
          label="Non-Binary"
          filters={filterList}
          wrapInGrid={false}
        />
        <FilterFacetCheckbox
          name="household_member_0_gender"
          facetValue="other"
          label="Other"
          filters={filterList}
          wrapInGrid={false}
        />
      </FormGroup>

      <Heading
        text="Last Status Change"
        name="heading_last_status_change"
        filters={filterList}
        tooltip="The date the status of the application was last changed."
      />
      <FilterDateRange
        label="Last Status Change"
        name="last_status_change"
        filters={filterList}
        containerStyle={{ maxWidth: 230 }}
      />

      <Heading text="Removal Reason" name="heading_removal_reason" filters={filterList} />
      <FilterMultiSelect
        name="current_removal_reason"
        label="Removal Reasons"
        options={[
          ...new Set([
            ...PossiblePropManagerRemovalReasons,
            ...PossibleClientRemovalReasons,
          ]),
        ]}
        filters={filterList}
      />

    </Grid>
  );
};

export default InterestsSidebarFilters;
